/**
 * THE BACK STACK: what the platform's Back gesture undoes, innermost first.
 *
 * A phone has a Back control the page never draws — the Mini App's own
 * button, the browser's gesture, the hardware key — and a surface raised OVER
 * the page is what that control must take down first: a sheet, a dialog, a
 * drilled-into level of a tree. Each one registers the way back while it
 * stands, and whoever owns the platform's control (`docs/web-reference/handheld.md`)
 * asks the stack rather than each surface.
 *
 * NOTHING HERE READS THE PLATFORM. The stack is the contract between a surface
 * and the bridge that will drive it; the bridge is a later owner's, and until
 * one is mounted the stack is absent and every registration is a no-op. That
 * is what keeps the desktop byte-for-byte: a dialog on a desktop registers
 * nothing, because no provider stands above it.
 *
 * The handler on top is called ONCE per Back and is then the surface's own
 * business to unregister, which it does by closing — the entry leaves the
 * stack when the surface that pushed it unmounts or stops being open.
 */
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react'

export interface BackStack {
  /** Registers the way back from a surface; returns the way to unregister it. */
  push(handler: () => void): () => void
  /** Takes the innermost surface down. Answers whether there was one. */
  back(): boolean
  /** How many surfaces stand between the reader and the page. */
  depth(): number
}

export function createBackStack(onDepthChange?: (depth: number) => void): BackStack {
  const stack: Array<() => void> = []
  const changed = () => onDepthChange?.(stack.length)
  return {
    push(handler) {
      stack.push(handler)
      changed()
      return () => {
        const at = stack.lastIndexOf(handler)
        if (at >= 0) {
          stack.splice(at, 1)
          changed()
        }
      }
    },
    back() {
      const top = stack[stack.length - 1]
      if (top === undefined) {
        return false
      }
      top()
      return true
    },
    depth() {
      return stack.length
    },
  }
}

export const BackStackContext = createContext<BackStack | null>(null)

export function BackStackProvider({ stack, children }: { stack: BackStack; children: ReactNode }) {
  return <BackStackContext.Provider value={stack}>{children}</BackStackContext.Provider>
}

export function useBackStack(): BackStack | null {
  return useContext(BackStackContext)
}

/**
 * ONE SURFACE'S ENTRY, held while `open`. The handler is read through a ref so
 * a caller may pass a fresh closure on every render without re-registering —
 * the stack ORDER is the thing that must not change under a re-render, since
 * the innermost surface is whichever registered last.
 */
export function useBackEntry(open: boolean, onBack: () => void): void {
  const stack = useBackStack()
  const latest = useRef(onBack)
  latest.current = onBack
  useEffect(() => {
    if (!open || stack === null) {
      return undefined
    }
    return stack.push(() => latest.current())
  }, [open, stack])
}

/** A stack for a host to create once and hand to the provider. */
export function useCreatedBackStack(onDepthChange?: (depth: number) => void): BackStack {
  const latest = useRef(onDepthChange)
  latest.current = onDepthChange
  return useMemo(() => createBackStack((depth) => latest.current?.(depth)), [])
}
